import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Check, CreditCard, Building2 } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Avatar } from '../components/Avatar';
import { Banner } from '../components/Banner';
import { Button } from '../components/Button';
import { apiPost } from '../api/client';
import { Card, CardHeader } from '../components/Card';
import { Badge } from '../components/Badge';
import { Money } from '../components/Money';
import { Modal } from '../components/Modal';
import { PageHeader } from '../components/PageHeader';
import { cn } from '../lib/cn';
import { formatDate, formatInt } from '../lib/format';
import { useActiveBusiness, useBusinesses, useSubscription, useSubscriptionsMap } from '../api/hooks';
import type { Subscription as SubscriptionT } from '../mocks/data';

type CancelReason = 'too_expensive' | 'missing_feature' | 'switching_tools' | 'closing' | 'other';

const CANCEL_REASONS: Array<{ value: CancelReason; label: string }> = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'missing_feature', label: 'Missing a feature' },
  { value: 'switching_tools', label: 'Switching tools' },
  { value: 'closing', label: 'Closing the business' },
  { value: 'other', label: 'Other' },
];

const PROVIDER_LABEL: Record<string, string> = {
  manual_cash: 'Cash',
  cash: 'Cash',
  sslcommerz: 'SSLCommerz',
  bkash: 'bKash',
  nagad: 'Nagad',
  bank_transfer: 'Bank transfer',
  credit_applied: 'Account credit',
};
const providerLabel = (p: string) => PROVIDER_LABEL[p] ?? p;

const MODULE_LABEL: Record<string, string> = {
  crm: 'CRM',
  hrm: 'HRM',
  essentials: 'Essentials (HRM)',
  repair: 'Repair',
  manufacturing: 'Manufacturing',
  woocommerce: 'WooCommerce sync',
  support: 'Support',
  rma: 'Returns (RMA)',
};

/** Build the "What's included" list from the subscription's real frozen entitlements. */
function includedItems(sub: SubscriptionT): string[] {
  const e = sub.entitlements;
  if (!e) return [];
  const cap = (n: number | null, singular: string, plural: string) =>
    n == null ? `Unlimited ${plural}` : `Up to ${formatInt(n)} ${n === 1 ? singular : plural}`;
  const items = [
    cap(e.maxLocations, 'business location', 'business locations'),
    cap(e.maxUsers, 'user account', 'user accounts'),
    cap(e.maxProducts, 'product', 'products'),
  ];
  if (e.maxInvoices != null) items.push(`Up to ${formatInt(e.maxInvoices)} invoices / month`);
  for (const m of e.modules) items.push(`${MODULE_LABEL[m] ?? m} module`);
  return items;
}

export function Subscription() {
  const { data: currentBusiness } = useActiveBusiness();
  const { data: businessesData } = useBusinesses();
  const { data: subscription } = useSubscription(currentBusiness?.id ?? '');
  const { data: subscriptionsMap } = useSubscriptionsMap();

  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState<CancelReason | null>(null);
  const [otherReason, setOtherReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [canceling, setCanceling] = useState(false);
  const [cancelResult, setCancelResult] = useState<{ ok: boolean; message: string } | null>(null);
  const canConfirmCancel = confirmText === 'CANCEL' && reason !== null;
  const queryClient = useQueryClient();

  async function confirmCancel() {
    if (!currentBusiness || !canConfirmCancel || canceling) return;
    setCanceling(true);
    try {
      const res = await apiPost<{ ok: boolean; cancelAtPeriodEnd: boolean; currentPeriodEnd: string | null }>(
        `/businesses/${currentBusiness.id}/subscription/cancel`,
        { reason: reason === 'other' ? otherReason.trim() || 'other' : reason },
      );
      setCancelResult({
        ok: true,
        message: res.currentPeriodEnd
          ? `Cancellation scheduled — your POS stays fully active until ${formatDate(res.currentPeriodEnd)}, then the subscription ends. No further charges.`
          : 'Cancellation scheduled — the subscription ends at the close of the current period. No further charges.',
      });
      void queryClient.invalidateQueries({ queryKey: ['subscription', currentBusiness.id] });
      setCancelOpen(false);
    } catch (err) {
      setCancelResult({ ok: false, message: err instanceof Error ? err.message : 'Could not schedule the cancellation. Please try again.' });
      setCancelOpen(false);
    } finally {
      setCanceling(false);
    }
  }

  if (!currentBusiness) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
        Loading subscription…
      </div>
    );
  }
  if (!subscription) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
        <PageHeader eyebrow={`Subscription for ${currentBusiness.name}`} title="No active subscription" subtitle="This business doesn't have an active plan yet." />
        <Card className="p-6 sm:p-8 text-center">
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4">Choose a plan to activate this business's subscription.</p>
          <Link to="/change-plan"><Button>Choose a plan</Button></Link>
        </Card>
      </div>
    );
  }
  const allBusinesses = businessesData ?? [];
  const otherBusinesses = allBusinesses.filter((b) => b.id !== currentBusiness.id);

  const STATUS_TONE = {
    active: 'success' as const,
    trial: 'info' as const,
    past_due: 'warning' as const,
    suspended: 'error' as const,
    canceled: 'neutral' as const,
  };
  const STATUS_LABEL = {
    active: 'Active',
    trial: 'Trial',
    past_due: 'Past due',
    suspended: 'Suspended',
    canceled: 'Canceled',
  };

  const included = includedItems(subscription);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow={`Subscription for ${currentBusiness.name}`}
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <span className={cn('w-9 h-9 rounded-md grid place-items-center flex-shrink-0', currentBusiness.iconColor)}>
              <Building2 className="w-5 h-5 text-white dark:text-zinc-950" />
            </span>
            <span>{currentBusiness.name}</span>
            <span className="text-base font-normal text-zinc-500 dark:text-zinc-400 mono">
              · {currentBusiness.id}
            </span>
          </span>
        }
        subtitle={
          <span>
            Plan and billing for this business. Each business has its own subscription —{' '}
            <Link to="/businesses" className="font-medium text-brand dark:text-brand-dm hover:underline">
              switch business
            </Link>{' '}
            to see another.
          </span>
        }
      />

      {cancelResult && (
        <div className="mb-4">
          <Banner
            tone={cancelResult.ok ? 'success' : 'error'}
            title={cancelResult.ok ? 'Cancellation scheduled' : 'Couldn’t cancel'}
            onDismiss={() => setCancelResult(null)}
          >
            {cancelResult.message}
          </Banner>
        </div>
      )}
      {(subscription as { cancelAtPeriodEnd?: boolean }).cancelAtPeriodEnd && !cancelResult && (
        <div className="mb-4">
          <Banner tone="warning" title="Cancellation scheduled">
            This subscription ends {subscription.renewsAt ? `on ${formatDate(subscription.renewsAt)}` : 'at the close of the current period'} and
            won't renew.{' '}
            <button
              type="button"
              className="font-medium underline"
              onClick={() => {
                void apiPost(`/businesses/${currentBusiness.id}/subscription/cancel`, { resume: true }).then(() => {
                  void queryClient.invalidateQueries({ queryKey: ['subscription', currentBusiness.id] });
                  setCancelResult({ ok: true, message: 'Cancellation reverted — the subscription will renew as usual.' });
                });
              }}
            >
              Resume the subscription
            </button>
          </Banner>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-3 sm:gap-4">
        {/* Current plan */}
        <Card className="lg:col-span-2 p-5 sm:p-6">
          <CardHeader
            title={
              <span className="flex items-center gap-2.5 flex-wrap">
                {subscription.plan}
                <span className="text-zinc-400 dark:text-zinc-500">·</span>
                <Money minor={subscription.amount} hideCents tone="neutral" />
                <span className="text-zinc-500 dark:text-zinc-400 text-sm font-normal">/ {subscription.interval}</span>
              </span>
            }
            subtitle={
              <span>
                Started {formatDate(subscription.startedAt)} · Renews{' '}
                <span className="tabular-nums font-medium text-zinc-700 dark:text-zinc-200">
                  {formatDate(subscription.renewsAt)}
                </span>
              </span>
            }
            action={<Badge tone={STATUS_TONE[subscription.status]}>{STATUS_LABEL[subscription.status]}</Badge>}
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-2">
            <UsageBar label="Locations" used={subscription.usage.locations.used} limit={subscription.usage.locations.limit} />
            <UsageBar label="Users" used={subscription.usage.users.used} limit={subscription.usage.users.limit} />
            <UsageBar label="Products" used={subscription.usage.products.used} limit={subscription.usage.products.limit} compact />
          </div>

          <div className="mt-6 pt-5 border-t border-zinc-200 dark:border-zinc-700 flex flex-wrap items-center gap-2">
            <Link to="/change-plan">
              <Button variant="secondary" size="sm">Change plan</Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={() => setCancelOpen(true)}>
              Cancel subscription
            </Button>
          </div>
        </Card>

        {/* Payment — most recent settled payment (no saved-card/auto-charge yet) */}
        <Card className="p-5 sm:p-6">
          <CardHeader title="Payment" />
          {subscription.lastPayment ? (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
              <span className="w-10 h-10 grid place-items-center rounded-md bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-700">
                <CreditCard className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50">{providerLabel(subscription.lastPayment.provider)}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
                  Last payment: <Money minor={subscription.lastPayment.amountMinor} hideCents tone="neutral" />
                  {subscription.lastPayment.paidAt ? ` · ${formatDate(subscription.lastPayment.paidAt)}` : ''}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 text-sm text-zinc-500 dark:text-zinc-400">
              No payments recorded yet.
            </div>
          )}
          <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
            Each renewal is invoiced to this business; pay it via SSLCommerz or cash.
          </p>
          <Link to="/billing">
            <Button variant="secondary" size="sm" fullWidth className="mt-3">View invoices</Button>
          </Link>
        </Card>
      </div>

      {/* Other businesses' subscriptions */}
      {otherBusinesses.length > 0 && (
        <Card className="mt-4 p-5 sm:p-6">
          <CardHeader
            title="Your other businesses"
            subtitle={`Each of your ${allBusinesses.length} businesses has its own subscription`}
            action={
              <Link
                to="/businesses"
                className="text-xs font-medium text-brand dark:text-brand-dm hover:underline inline-flex items-center gap-1"
              >
                Manage all <ArrowRight className="w-3 h-3" />
              </Link>
            }
          />
          <div className="grid sm:grid-cols-2 gap-3">
            {otherBusinesses.map((b) => {
              const sub = subscriptionsMap?.[b.id];
              if (!sub) return null;
              return (
                <Link
                  key={b.id}
                  to="/businesses"
                  className="group flex items-center gap-3 p-3 rounded-lg border border-zinc-200 dark:border-zinc-600 hover:border-zinc-300 dark:hover:border-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition"
                >
                  <Avatar name={b.name} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">
                        {b.name}
                      </span>
                      <Badge tone={STATUS_TONE[sub.status]}>{STATUS_LABEL[sub.status]}</Badge>
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 tabular-nums">
                      {sub.plan} ·{' '}
                      <Money minor={sub.amount} hideCents tone="neutral" className="text-zinc-700 dark:text-zinc-200" />
                      {' '}/ {sub.interval} · renews {formatDate(sub.renewsAt, { compact: true })}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-700 dark:group-hover:text-zinc-200 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        </Card>
      )}

      {/* What's included — from the plan's real frozen entitlements */}
      {included.length > 0 && (
        <Card className="mt-4 p-5 sm:p-6">
          <CardHeader title={`What's included in ${subscription.plan}`} subtitle="Your plan's entitlements" />
          <div className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
            {included.map((item) => (
              <div key={item} className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-200">
                <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Cancel modal */}
      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancel subscription"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>
              Keep subscription
            </Button>
            <Button
              variant="destructive"
              disabled={!canConfirmCancel}
              loading={canceling}
              onClick={() => void confirmCancel()}
            >
              Cancel subscription
            </Button>
          </>
        }
      >
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          We're sorry to see you go. Help us improve by sharing why.
        </p>

        <fieldset className="mt-4">
          <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            Reason for leaving
          </legend>
          <div className="space-y-2">
            {CANCEL_REASONS.map((r) => (
              <label
                key={r.value}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition',
                  reason === r.value
                    ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30'
                    : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600',
                )}
              >
                <input
                  type="radio"
                  name="cancel-reason"
                  value={r.value}
                  checked={reason === r.value}
                  onChange={() => setReason(r.value)}
                  className="text-brand focus:ring-brand"
                />
                <span className="text-sm text-zinc-900 dark:text-zinc-100">{r.label}</span>
              </label>
            ))}
          </div>
          {reason === 'other' && (
            <textarea
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              placeholder="Tell us a bit more (optional)…"
              rows={2}
              className="mt-3 block w-full px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand dark:focus:ring-brand-dm dark:focus:border-brand-dm transition resize-none"
            />
          )}
        </fieldset>

        <div className="mt-5 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 p-4">
          <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            What happens next
          </div>
          <ul className="space-y-1.5 text-sm text-zinc-600 dark:text-zinc-300">
            <li className="flex gap-2">
              <span className="text-zinc-400 dark:text-zinc-500">·</span>
              Your POS keeps working until <strong className="text-zinc-800 dark:text-zinc-100">{formatDate(subscription.renewsAt)}</strong> (period end).
            </li>
            <li className="flex gap-2">
              <span className="text-zinc-400 dark:text-zinc-500">·</span>
              After that, your account is suspended for 30 days. You can reactivate any time.
            </li>
            <li className="flex gap-2">
              <span className="text-zinc-400 dark:text-zinc-500">·</span>
              After 30 days suspended, your data is permanently deleted.
            </li>
            <li className="flex gap-2">
              <span className="text-zinc-400 dark:text-zinc-500">·</span>
              You can export everything as JSON before then.
            </li>
          </ul>
        </div>

        <div className="mt-5">
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
            Type <span className="mono font-semibold">CANCEL</span> to confirm
          </label>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="CANCEL"
            className="block w-full px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition mono tabular-nums uppercase"
            autoComplete="off"
          />
        </div>
      </Modal>
    </div>
  );
}

function UsageBar({
  label,
  used,
  limit,
  compact = false,
}: {
  label: string;
  used: number;
  limit: number;
  compact?: boolean;
}) {
  const unlimited = !limit || limit <= 0; // limit 0 = unlimited / unset
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-zinc-700 dark:text-zinc-200">{label}</span>
        <span className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
          {unlimited ? `${compact ? formatInt(used) : used} / ∞` : compact ? `${formatInt(used)} / ${formatInt(limit)}` : `${used} / ${limit}`}
        </span>
      </div>
      <div className="h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-500 ease-out',
            pct > 90 ? 'bg-rose-500' : pct > 75 ? 'bg-amber-500' : 'bg-brand dark:bg-brand-dm',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
